import toast from "react-hot-toast";
import api from "../../libs/axios";

/**
 * Fetch a binary INC endpoint (xlsx / zip) and hand it to the browser as a
 * download.
 *
 * These routes are permission-gated, so they can't just be opened in a new tab —
 * the request has to carry the auth header. On failure the server still replies
 * with the normal JSON error envelope, which arrives here as a Blob; it is read
 * back to text so the user sees the real message ("No files uploaded for this
 * selection") instead of a generic failure.
 */
export async function downloadIncFile(
  url: string,
  fallbackFileName: string,
  params?: Record<string, any>,
): Promise<void> {
  try {
    const response = await api.get(url, { params, responseType: "blob" });

    // Prefer the server's filename so the visit/site naming survives.
    const disposition = String(response.headers?.["content-disposition"] || "");
    const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
    const fileName = match ? decodeURIComponent(match[1]) : fallbackFileName;

    const blobUrl = window.URL.createObjectURL(response.data as Blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    let message = "Download failed";
    const data = error?.response?.data;
    if (data instanceof Blob) {
      try {
        message = JSON.parse(await data.text())?.message || message;
      } catch {
        /* not JSON — keep the generic message */
      }
    } else if (data?.message) {
      message = data.message;
    }
    toast.error(message);
  }
}
